/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import LangSwitch from "@/components/LangSwitch";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  LogOut, Users, BarChart3, Home, Shield, UserCog,
  History, ShieldCheck, Menu, Inbox, BookOpen, Facebook,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const FACEBOOK_URL = "https://www.facebook.com/share/182xo56fwH/";

function NavList({ items, currentPath, onItemClick }) {
  return (
    <nav className="flex-1 space-y-1.5">
      {items.map((it) => {
        const Icon = it.icon;
        const active = currentPath === it.href;
        return (
          <Link
            key={it.href}
            to={it.href}
            onClick={onItemClick}
            data-testid={`nav-${it.href.replace(/\W+/g, "-")}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
              active ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
            }`}
          >
            <Icon size={18} />
            {it.label}
          </Link>
        );
      })}
    </nav>
  );
}

function UserCard({ user, roleLabel, logoutLabel, onLogout }) {
  return (
    <div className="border-t border-white/10 pt-5 mt-5">
      <div className="flex items-center gap-3 px-2 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#E05A3D] grid place-items-center text-white font-bold text-sm">
          {user.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{user.name}</div>
          <div className="text-xs text-white/60 flex items-center gap-1.5">
            <Shield size={11} />
            {roleLabel}
          </div>
        </div>
      </div>
      <Button
        data-testid="logout-btn"
        onClick={onLogout}
        variant="ghost"
        className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
      >
        <LogOut size={16} className="me-2" />
        {logoutLabel}
      </Button>
      <a
        href={FACEBOOK_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="dashboard-facebook-link"
        aria-label="Suivez-nous sur Facebook"
        className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-xs font-medium transition-colors"
      >
        <Facebook size={14} />
        Suivez-nous sur Facebook
      </a>
    </div>
  );
}

export default function DashboardShell({ children, allowedRoles = [] }) {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF7]">
        <div className="text-[#5C6B5C] text-sm">{t.app.common.loading}</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (allowedRoles.length && !allowedRoles.includes(user.role)) {
    const redirect =
      user.role === "owner" ? "/dashboard/owner"
        : user.role === "trainer" ? "/dashboard/trainer" : "/";
    return <Navigate to={redirect} replace />;
  }

  const isOwner = user.role === "owner";
  const navItems = [
    { href: isOwner ? "/dashboard/owner" : "/dashboard/trainer", label: t.app.shell.overview, icon: BarChart3 },
    { href: isOwner ? "/dashboard/owner/clients" : "/dashboard/trainer/clients", label: t.app.shell.clients, icon: Users },
    { href: isOwner ? "/dashboard/owner/expert-requests" : "/dashboard/trainer/expert-requests", label: t.app.shell?.expert_inbox || "File experts", icon: Inbox },
    ...(isOwner
      ? [
          { href: "/dashboard/owner/trainers", label: t.app.shell.trainers, icon: UserCog },
          { href: "/dashboard/owner/best-practices", label: "Fiches", icon: BookOpen },
          { href: "/dashboard/owner/audit", label: t.app.shell.audit, icon: History },
        ]
      : []),
    { href: isOwner ? "/dashboard/owner/security" : "/dashboard/trainer/security", label: t.app.shell.security, icon: ShieldCheck },
    { href: "/", label: t.app.shell.landing, icon: Home },
  ];

  const items = navItems;

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-72 bg-[#1C3A27] text-white p-6">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-2" data-testid="dashboard-brand">
            <span className="w-9 h-9 rounded-full bg-white grid place-items-center">
              <span className="w-3.5 h-3.5 rounded-full bg-[#E05A3D]" />
            </span>
            <span className="font-display text-xl font-bold">Mazra3ti</span>
          </Link>
          <LangSwitch />
        </div>
        <NavList items={items} currentPath={location.pathname} />
        <UserCard user={user} roleLabel={t.app.role[user.role]} logoutLabel={t.app.common.logout} onLogout={logout} />
      </aside>

      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#1C3A27] text-white px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="w-10 h-10 rounded-lg grid place-items-center hover:bg-white/10"
          >
            <Menu size={20} />
          </button>
          <Link to="/" className="font-display text-base font-bold">Mazra3ti</Link>
        </div>
        <div className="flex items-center gap-1">
          <LangSwitch />
        </div>
      </div>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent
          side="left"
          className="w-80 max-w-[85vw] bg-[#1C3A27] text-white border-r-0 p-6 flex flex-col"
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-white grid place-items-center">
                <span className="w-3.5 h-3.5 rounded-full bg-[#E05A3D]" />
              </span>
              <span className="font-display text-xl font-bold">Mazra3ti</span>
            </div>
          </div>
          <NavList items={items} currentPath={location.pathname} onItemClick={() => setDrawerOpen(false)} />
          <UserCard user={user} roleLabel={t.app.role[user.role]} logoutLabel={t.app.common.logout} onLogout={logout} />
        </SheetContent>
      </Sheet>

      <main className="flex-1 pt-14 lg:pt-0 w-full min-w-0">
        <div className="px-4 sm:px-6 md:px-10 py-8 md:py-12 max-w-7xl">{children}</div>
      </main>
    </div>
  );
}
